import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosService from '../services/axios.service';
import { Subscription, CreateSubscriptionDto, UpdateSubscriptionDto } from '../../../shared/types';

const API_BASE = '/api/admin/subscriptions';

interface SubscriptionWithCrew extends Subscription {
  crew: {
    _id: string;
    name: string;
    slug: string;
    status: string;
  } | null;
}

export function useSubscriptions() {
    return useQuery<{ success: boolean; data: SubscriptionWithCrew[] }>({
        queryKey: ['admin', 'subscriptions'],
        queryFn: async () => {
            const { data } = await axios.get(API_BASE);
            return data;
        }
    });
}

export function useSubscription(crewSlug: string) {
    return useQuery({
        queryKey: ['admin', 'subscriptions', crewSlug],
        queryFn: async () => {
            const { data } = await axios.get(`${API_BASE}/${crewSlug}`);
            return data;
        },
        enabled: !!crewSlug
    });
}

export function useCreateSubscription() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CreateSubscriptionDto) => {
            const { data } = await axios.post(API_BASE, dto);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
        }
    });
}

export function useUpdateSubscription() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ crewSlug, dto }: { crewSlug: string; dto: UpdateSubscriptionDto }) => {
            const { data } = await axios.put(`${API_BASE}/${crewSlug}`, dto);
            return data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions', variables.crewSlug] });
        }
    });
}

export function useCancelSubscription() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ crewSlug, immediately }: { crewSlug: string; immediately?: boolean }) => {
            const { data } = await axios.post(`${API_BASE}/${crewSlug}/cancel`, { immediately });
            return data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions', variables.crewSlug] });
        }
    });
}

export function useReactivateSubscription() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (crewSlug: string) => {
            const { data } = await axios.post(`${API_BASE}/${crewSlug}/reactivate`);
            return data;
        },
        onSuccess: (_, crewSlug) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions', crewSlug] });
        }
    });
}

/** Get the Stripe hosted payment URL for the crew's latest open invoice */
export function useGetInvoicePaymentLink() {
    return useMutation({
        mutationFn: async (crewSlug: string) => {
            const { data } = await axios.post(`${API_BASE}/${crewSlug}/invoice-link`);
            return data as { success: boolean; data: { url: string; invoiceId?: string } };
        }
    });
}

// ─── Crew-scoped hooks ────────────────────────────────────────────────────────

export interface CrewSubscriptionStatus {
  hasSubscription: boolean;
  status: 'active' | 'canceled' | 'past_due' | null;
  tierId: string | null;
}

export function useCrewCancelSubscription(crewSlug: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosService.post(`/c/${crewSlug}/subscription/cancel`);
            return data as { success: boolean; message: string };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crew', 'subscription', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['crew', 'subscription', 'status', crewSlug] });
        }
    });
}

export function useCrewUpgradeSubscription(crewSlug: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tierId: 'pro' | 'enterprise') => {
            const { data } = await axiosService.post(`/c/${crewSlug}/subscription/upgrade`, { tierId });
            return data as { success: boolean; data: object };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crew', 'subscription', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['crew', 'subscription', 'status', crewSlug] });
        }
    });
}

export function useCrewSubscriptionStatus(crewSlug?: string) {
    return useQuery<{ success: boolean; data: CrewSubscriptionStatus }>({
        queryKey: ['crew', 'subscription', 'status', crewSlug],
        queryFn: async () => {
            const { data } = await axiosService.get(`/c/${crewSlug}/subscription/status`);
            return data;
        },
        enabled: !!crewSlug,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false
    });
}

export function useCrewSubscription(crewSlug?: string) {
    return useQuery<{ success: boolean; data: Subscription | null }>({
        queryKey: ['crew', 'subscription', crewSlug],
        queryFn: async () => {
            const { data } = await axiosService.get(`/c/${crewSlug}/subscription`);
            return data;
        },
        enabled: !!crewSlug,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false
    });
}

export function useCrewOutstandingInvoice(crewSlug?: string) {
    return useQuery<{ success: boolean; data: { hasOutstandingInvoice: boolean; invoiceUrl?: string | null; amountDue?: number | null } }>({
        queryKey: ['crew', 'subscription', 'invoice', crewSlug],
        queryFn: async () => {
            const { data } = await axiosService.get(`/c/${crewSlug}/subscription/outstanding-invoice`);
            return data;
        },
        enabled: !!crewSlug,
        staleTime: 1000 * 60 * 2,
        refetchOnWindowFocus: true
    });
}
